import { InfiniteData } from '@tanstack/react-query';
import { deviceTypesWithAccurateBatteryPercentage } from '@ixicare/resources';
import { formatDate, getCurrentDate, IconButton, OfflineDevice } from '@ixicare/ui';
import BatteryIndicator from '~components/BatteryIndicator';
import { Link } from 'react-router-dom';
import { Map } from 'lucide-react';
import React from 'react';
import { t } from '~i18n';
import { WEARABLE_TYPES } from '~modules/devices/utils/constants';
import { Status, UnifiedDBG } from './types';
import SiteStore from '~modules/sites/store/Site';
import { BaseTableConfig } from '~components/Tables/DeviceTables/types';

type StatusEntry = {
  config: BaseTableConfig & { columnIds: string[] };
  data: any[][];
};

// Display rows hold JSX cells for things like <BatteryIndicator/> and
// <OfflineDevice/>. Sorting those by `<` is undefined behaviour, so we keep a
// parallel raw-value row (numbers, timestamps, strings) and sort against that.
type GroupedEntry = StatusEntry & { rawData: any[][] };

interface DeviceHealthDataFormatterProps {
  data: (InfiniteData<any> & { flatData?: UnifiedDBG[] }) | undefined;
  site?: SiteStore | null;
}

enum Columns {
  MAC_ADDRESS,
  DESCRIPTION,
  DEVICE_TYPE,
  SOFTWARE_VERSION,
  BATTERY_LEVEL,
  LAST_SEEN,
  STATUS,
  MAP,
}

const determineColumns = (status: Status): Array<any> => {
  const columns = Object.keys(Columns)
    .filter((key) => isNaN(Number(key)))
    .filter((col) => col !== 'SOFTWARE_VERSION' && col !== 'BATTERY_LEVEL' && col !== 'STATUS');

  if (status == Status.LOW_BATTERY) {
    columns.splice(-2, 0, 'BATTERY_LEVEL');
  }

  if (status == Status.OUTDATED) {
    columns.splice(-2, 0, 'SOFTWARE_VERSION');
  }

  return columns;
};

export const useDeviceHealthDataFormatter = ({ data, site }: DeviceHealthDataFormatterProps) => {
  const [orderBy, setOrderBy] = React.useState<string>('macAddress');
  const [ascOrder, setAscOrder] = React.useState(false);

  const handleSort = (columnId: string) => {
    if (!columnId) return;
    if (orderBy === columnId) {
      setAscOrder((x) => !x);
    } else {
      setOrderBy(columnId);
    }
  };

  const groupedData = React.useMemo<{ [k: string]: GroupedEntry } | undefined>(() => {
    if (!data) return;

    // Each entry has `display` (renderable, may be JSX) and `raw` (sortable
    // primitives) for every column. Same keys on both objects.
    const mappedData =
      data?.flatData?.map((x) => {
        const deviceType = x.type && t(`device.types.${x.type.toLowerCase()}`);
        const lastSeenTs = x?.lastSeen ? new Date(x.lastSeen).getTime() : null;
        return {
          status: x.status as Status | undefined,
          raw: {
            macAddress: x.macAddress ?? null,
            description: x.description ?? null,
            deviceType: deviceType ?? null,
            softwareVersion: x.swVersion ?? null,
            batteryLevel: typeof x.batteryLevel === 'number' ? x.batteryLevel : null,
            lastSeen: lastSeenTs,
            map: x?.installationCoords ? 1 : 0,
          } as Record<string, string | number | null>,
          display: {
            macAddress: x.macAddress ? (
              <Link to={`/device-overview?q=${x.macAddress}#deviceFinder`}>{x.macAddress}</Link>
            ) : undefined,
            description: x.description,
            deviceType,
            softwareVersion: x.swVersion,
            batteryLevel:
              typeof x.batteryLevel === 'number' ? (
                <BatteryIndicator
                  inaccurate={!deviceTypesWithAccurateBatteryPercentage.includes(x.type)}
                  percentage={x.batteryLevel as number}
                  chargerConnected={x.chargerConnected}
                />
              ) : undefined,
            lastSeen: x?.lastSeen ? (
              formatDate(x?.lastSeen) === getCurrentDate() ? (
                formatDate(x?.lastSeen)
              ) : (
                <OfflineDevice date={x?.lastSeen} />
              )
            ) : undefined,
            map: x?.installationCoords ? (
              <Link
                to={`/sites/${site?.uuid}/${x.planeId}?marker=${encodeURIComponent(`${x.type.toLowerCase()}::${x.id}`)}`}
              >
                <IconButton lucide={Map} />
              </Link>
            ) : !WEARABLE_TYPES.includes(x.type) ? (
              '!'
            ) : (
              ''
            ),
          } as Record<string, any>,
        };
      }) ?? [];

    const rowsPerStatus = Object.values(Status).reduce(
      (acc, current) => ((acc[current] = { columns: [], data: [], rawData: [] }), acc),
      {} as { [key in Status]: { columns: string[]; data: any[][]; rawData: any[][] } }
    );

    const snakeToCamelCase = (s) => s.toLowerCase().replace(/[-_][a-z]/g, (group) => group.slice(-1).toUpperCase());

    const getTranslationForColumn = (c: string) => t(`devices.table.${c}`);

    mappedData.forEach((row) => {
      const status = row.status;
      if (!status || !rowsPerStatus[status]) return;
      if (!rowsPerStatus[status].columns.length) {
        rowsPerStatus[status].columns = determineColumns(status);
      }
      const requiredColumns = rowsPerStatus[status].columns.map((c) => snakeToCamelCase(c));
      rowsPerStatus[status].data.push(requiredColumns.map((c) => row.display[c]));
      rowsPerStatus[status].rawData.push(requiredColumns.map((c) => row.raw[c]));
    });

    const formatedData: { [k: string]: GroupedEntry } = Object.fromEntries(
      Object.entries(rowsPerStatus).map(([key, val]) => {
        const columnIds = val.columns.map((c) => snakeToCamelCase(c));
        const translatedColumns = columnIds.map((c) => getTranslationForColumn(c));
        return [
          key.replace(/_/g, ' '),
          {
            data: val.data,
            rawData: val.rawData,
            config: {
              tableId: `device-health-${key.toLowerCase()}`,
              columns: translatedColumns,
              columnIds,
            },
          },
        ];
      })
    );

    return formatedData;
  }, [data, site]);

  const compareRaw = (valA: unknown, valB: unknown, asc: boolean) => {
    const order = asc ? 1 : -1;
    if (valA == null && valB == null) return 0;
    if (valA == null) return order * -1;
    if (valB == null) return order * 1;
    if (typeof valA === 'string' && typeof valB === 'string') {
      return valA.localeCompare(valB, undefined, { sensitivity: 'base' }) * order;
    }
    if ((valA as number) < (valB as number)) return order * -1;
    if ((valA as number) > (valB as number)) return order * 1;
    return 0;
  };

  const sortedData = React.useMemo<{ [k: string]: StatusEntry }>(() => {
    if (!groupedData) return {};
    return Object.fromEntries(
      Object.entries(groupedData).map(([key, val]) => {
        const sortIndex = val.config.columnIds.indexOf(orderBy);
        if (sortIndex < 0) return [key, { config: val.config, data: val.data }];
        // Sort indices by raw value, then apply the same permutation to the
        // display rows so JSX cells follow their raw sort key.
        const indices = val.data.map((_, i) => i);
        indices.sort((a, b) => compareRaw(val.rawData[a][sortIndex], val.rawData[b][sortIndex], ascOrder));
        return [key, { config: val.config, data: indices.map((i) => val.data[i]) }];
      })
    );
  }, [groupedData, orderBy, ascOrder]);

  const summaryRow = React.useMemo(() => {
    if (!groupedData) return [];
    return [Object.values(groupedData).map((val) => (val ? val.data.length : 0))];
  }, [groupedData]);

  const outputStatuses = React.useMemo(() => {
    if (!sortedData) return [];
    const populatedStatuses = Object.entries(sortedData).filter(([, rows]) => rows?.data.length > 0);

    if (populatedStatuses[0] && populatedStatuses[0][0] === 'GREEN') {
      const greenStatus = populatedStatuses.shift();
      if (greenStatus) {
        populatedStatuses.push(greenStatus);
      }
    }

    const PER_ROW = 2;
    const chunkedRows: [string, StatusEntry][][] = [];
    for (let i = 0; i < populatedStatuses.length; i += PER_ROW) {
      chunkedRows.push(populatedStatuses.slice(i, i + PER_ROW) as [string, StatusEntry][]);
    }
    return chunkedRows;
  }, [sortedData]);

  return {
    summaryRow,
    outputStatuses,
    handleSort,
    orderBy,
    ascOrder,
  };
};
